import AuthenticationService from '@/services/AuthenticationService';
import router from '@/router';
import { i18n } from '@/i18n';

// Helper-Funktion außerhalb des Store
function getCustomLogoUrl() {
  if (APP_TARGET === "bmw")
    return window.location.hostname.includes("bmwmotorrad")
      ? '/static/img/bmw_motorrad_logo_2021.svg'
      : require('@/assets/img-bmw/v3/logo.png');
  else if (APP_TARGET === "lambo") return '/static/img/logo.lamborghini.415x70px.png';
  else if (APP_TARGET === "aldisued") return '/static/img/logo.aldisued.83x100px.svg';
  else if (APP_TARGET === "ford") return '/static/img/logo-ford.png';
  else if (APP_TARGET === "jaguar") return '/static/img/logo-jaguar.png';
  else if (APP_TARGET === "obi") return '/static/img/Obi_366px.png';
  else if (APP_TARGET === "obinext") return '/static/img/obi-next-logo.png';
  else if (APP_TARGET === "porsche") return '/static/img/logo.porsche.415x70px.png';
  else if (APP_TARGET === "porschesar") return '/static/img/logo.porsche.415x70px.png';
  else if (APP_TARGET === "audi") return '/static/img/Audi_Logo.png';
  else if (APP_TARGET === "db") return '/static/img/Logo_ServiceStoreDB.png';
  else if (APP_TARGET === "kia") return '/static/img/logo-light.png';
  else if (APP_TARGET === "genesis") return '/static/img/logo.png';
  else return null;
}

const state = {
  clientId: null,
  isAuthenticated: false,
  isMSMUser: false,
  logoUrl: null,
  user: null,
};

const getters = {
  user: state => state.user,
  isAuthenticated: state => state.isAuthenticated,
  isMSMUser: state => state.isMSMUser,
  isExternalUser: state => AuthenticationService.isExternalUser,
  isExternalIdentityProvider: state => AuthenticationService.isExternalIdentityProvider,
  clientId: state => state.clientId,

  // Convenience getters
  hasClient: state => !!state.clientId,
  userProfile: state => state.user,
  userDisplayName: state => {
    if (!state.user) return '';
    return `${AuthenticationService.localUserProfile.FirstName || ''} ${AuthenticationService.localUserProfile.LastName || ''}`.trim();
  },
  userInitials: state => {
    if (!AuthenticationService.localUserProfile) return '  ';
    const firstInitial = AuthenticationService.localUserProfile.FirstName ? AuthenticationService.localUserProfile.FirstName.charAt(0).toUpperCase() : '';
    const lastInitial = AuthenticationService.localUserProfile.LastName ? AuthenticationService.localUserProfile.LastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}` || 'U';
  },
  userCompany: state => {
    if (APP_TARGET === "bmw") {
      return 'BMW';
    }
    if (APP_TARGET === "lexus") {
      return 'LEXUS';
    }
    if (APP_TARGET === "genesis") {
      return 'GENESIS';
    }
    if (APP_TARGET === "kia") {
      return 'KIA Deutschland';
    }
    return AuthenticationService.localUserProfile ? AuthenticationService.localUserProfile.Company || 'Company' : 'Company';
  },
  logoUrl: state => state.logoUrl,
  isCXReporting: state => AuthenticationService.localUserProfile.Permissions.includes("360Reporting"),
  isGenericReporting: state => (APP_TARGET === "" || APP_TARGET === "training"),
  areLocationNotificationsVisible: state => APP_TARGET === 'db',
  isCustomDashboardAvailable: state => {
    if (APP_TARGET === "porschesar")
      return AuthenticationService.localUserProfile && AuthenticationService.localUserProfile.Permissions.includes('CustomDashboard');
    else
      return true;
  },
  hasMarketReports: state => {
    if (APP_TARGET === "audi") {
      return AuthenticationService.localUserProfile.Permissions.includes('Market Reports')
        || AuthenticationService.localUserProfile.Permissions.includes('Market Reports Admin');
    }
    else return false;
  },
  isDemo: state => {
    if (APP_TARGET === "ford") return true;
    else if (APP_TARGET === "jaguar") return true;
    else if (APP_TARGET === "demo") return true;
    else return false;
  },
  hasMapView: state => {
    if (APP_TARGET === "bmw") return true;
    else return false;
  },
  getMapsApiKey: state => {
    if (APP_TARGET === "bmw") return process.env.VUE_APP_BMW_GOOGLEMAPS_API_KEY;
    else return "KEY NOT CONFIGURED!!!";
  },
  hasDashboard: state => true,
  hasDashboardCustomerSelection: state => {
    if (APP_TARGET === "obi") return true;
    else return false;
  },
  isDashboardTotalScoreWidgetEnabled: state => {
    if (APP_TARGET === "db") return false;
    else return true;
  },
  hiddenResultKpiGroups: state => {
    if (APP_TARGET === "db") return ['Zielerreichung'];
    else return [];
  },
  hasCheckExplorer: state => {
    if (APP_TARGET === "renault") return false;
    else return true;
  },
  hasCheckExplorerPermission: state => {
    return AuthenticationService.localUserProfile.Permissions.includes('CheckExplorer');
  },
  hasComparision: state => {
    if (APP_TARGET === "bmw") {
      if (!window.location.hostname.includes("bmwmotorrad")) {
        return true;
      }
    }
    return false;
  },
  hasStaticDocument: state => (type) => {
    if (type === 'kpi-explanation' && window.location.hostname.includes("bmwmotorrad"))
      return true;
    return false;
  },

  isKpiDefaultMode: state => {
    if (APP_TARGET === "db") return true;
    else return false;
  },

  hasRawDataPointSelection: state => {
    if (APP_TARGET === "audi") return false;
    else return true;
  },

  hasSurveyInstanceProofs: state => {
    if (APP_TARGET === "audi" ||
        APP_TARGET === "auditraining" ||
        APP_TARGET === "db" ||
        APP_TARGET === "porschesar" ||
        APP_TARGET === "porschesartraining"
    ) return false;
    return true;
  }
};

const mutations = {
  SET_CLIENT_ID(state, clientId) {
    if (state.clientId === clientId)
      return;
    state.clientId = clientId;
  },

  SET_LOGO_URL(state, logoUrl) {
    state.logoUrl = logoUrl;
  },

  SET_USER_PROFILE(state, profile) {
    state.user = profile;
    // state.clientId = APP_TARGET === "bmw" ? 181 : (profile?.ClientId || null);
    // TODO: ClientId und ProjectId ggf. anders setzen
    // state.clientId = profile?.ClientId || null;
    i18n.global.locale = profile?.LocalizationId || i18n.global.locale;
  },

  SET_AUTHENTICATION_STATUS(state, { isAuthenticated, isMSMUser }) {
    state.isAuthenticated = isAuthenticated;
    state.isMSMUser = isMSMUser;
  },

  CLEAR_USER_PROFILE(state) {
    state.user = null;
    state.isAuthenticated = false;
    state.isMSMUser = false;
    state.clientId = null;
    state.localizationId = null;
  }
};

const actions = {
  async initializeAuth({ commit }) {
    try {
      // Lade User Profile vom Service
      const profile = AuthenticationService.localUserProfile;

      if (profile) {
        commit('SET_USER_PROFILE', profile);
        commit('SET_AUTHENTICATION_STATUS', {
          isAuthenticated: AuthenticationService.isAuthenticated,
          isMSMUser: AuthenticationService.isMSMUser
        });
        try {
          const logoUrl = getCustomLogoUrl() || AuthenticationService.logoExternalImageUrl;
          commit('SET_LOGO_URL', logoUrl);
         } catch (error) {
            log.error('STO: Failed to set logo URL:', error);
         }

      }
    } catch (error) {
      log.error('STO: Failed to initialize auth:', error);
      commit('CLEAR_USER_PROFILE');
    }
  },

  async updateUserProfile({ commit }, updates) {
    try {
      // Update über Service
      await AuthenticationService.updateProfile(updates);

      // Update Store
      const updatedProfile = { ...AuthenticationService.localUserProfile, ...updates };
      commit('SET_USER_PROFILE', updatedProfile);
    } catch (error) {
      log.error('STO: Failed to update profile:', error);
      throw error;
    }
  },

  logout({ commit }) {
    AuthenticationService.logout();
    commit('CLEAR_USER_PROFILE');
    router.push('/logout');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
